import { FieldDescription } from '@HoudiniUI/Field';
import { Label } from '@HoudiniUI/Label';
import * as Api from '@ViewModels';
import { Toggle } from '@WebComponents/Toggle';
import { css } from 'aphrodite';
import React from 'react';
import { EditModeCombobox, useEditMode } from '../context';
import type { WritebackGridSchema } from '../providers/types';
import { getFieldOptions, getSelectDisplayValue, getSettingValue } from '../providers/utils';
import { styleSheet } from '../styles';

const WRITE_BACK_TYPE_LABELS: Record<Api.IntegrationWriteBackType, string> = {
	[Api.IntegrationWriteBackType.Email]: 'Email',
	[Api.IntegrationWriteBackType.Note]: 'Note',
	[Api.IntegrationWriteBackType.PhoneCall]: 'Phone Call',
	[Api.IntegrationWriteBackType.TextMessage]: 'Text Message',
	[Api.IntegrationWriteBackType.HandwrittenCard]: 'Handwritten Card',
};

interface IWritebackSettingsGridProps {
	schema: WritebackGridSchema;
	settings: Api.IIntegrationSettings;
	configuration: Api.IIntegrationConfiguration;
	displayName: string;
	onUpdate: (updates: Record<string, unknown>) => void;
}

export const WritebackSettingsGrid: React.FC<IWritebackSettingsGridProps> = ({
	schema,
	settings,
	configuration,
	displayName,
	onUpdate,
}) => {
	const { isEditing } = useEditMode();
	const availableTypes = configuration.availableWriteBackTypes ?? [];
	const enabledTypes = settings.writeBacks ?? [];
	const codes = getSettingValue<Record<string, string>>(settings, schema.settingsKey) ?? {};
	const options = getFieldOptions(schema.optionsFrom, { configuration });

	// An enabled type the configuration no longer offers still needs a row, or there is no way to turn it off.
	const rowTypes = [...availableTypes, ...enabledTypes.filter(type => !availableTypes.includes(type))];

	// The code is left alone so a re-enable doesn't lose the mapping; the API ignores codes for disabled types.
	const setEnabled = (writeBackType: Api.IntegrationWriteBackType, isEnabled: boolean) =>
		onUpdate({
			writeBacks: isEnabled ? [...enabledTypes, writeBackType] : enabledTypes.filter(type => type !== writeBackType),
		});

	const setCode = (writeBackType: Api.IntegrationWriteBackType, code: string) => {
		const next = { ...codes };
		if (code) {
			next[writeBackType] = code;
		} else {
			// The API reads an absent type as uncoded; an empty string would persist a mapping that means nothing.
			delete next[writeBackType];
		}
		onUpdate({ [schema.settingsKey]: next });
	};

	const renderCode = (writeBackType: Api.IntegrationWriteBackType) => {
		if (!enabledTypes.includes(writeBackType)) {
			return <span className={css(styleSheet.writebackRowInactive)}>Not written back</span>;
		}

		const code = codes[writeBackType] ?? '';

		if (!options.length) {
			// Without this the row reads as unmapped, and the saved code can be neither seen nor cleared.
			return code ? (
				<>
					<span className={css(styleSheet.writebackRowMuted)}>{code}</span>
					<FieldDescription styles={[styleSheet.writebackRowDescription]}>{schema.emptyOptionsLabel}</FieldDescription>
				</>
			) : (
				<span className={css(styleSheet.writebackRowMuted)}>{schema.emptyOptionsLabel}</span>
			);
		}

		const isMissing = !code;
		// A code the provider has since dropped resolves to no option, so the picker would render the placeholder and
		// read as unmapped while the stale code stays in use. Carry it as its own option so it can be seen and cleared.
		const isStale = !isMissing && !options.some(option => option.id === code);
		const items = isStale ? [...options, { id: code, label: code }] : options;
		const hint = isStale ? `No longer available in ${displayName}` : isMissing ? schema.missingCodeHint : undefined;

		return (
			<>
				<EditModeCombobox
					multiple={false}
					items={items}
					value={code}
					onChange={(newCode: string) => setCode(writeBackType, newCode)}
					displayValue={code ? getSelectDisplayValue(code, items) : 'None selected'}
					searchPlaceholder={schema.placeholder}
					customStyles={[styleSheet.writebackRowReadOnlyCode]}
					editStyles={[styleSheet.writebackRowCodeField]}
					triggerStyles={isMissing || isStale ? [styleSheet.writebackRowCodeMissing] : []}
				/>
				{hint ? (
					<FieldDescription styles={[styleSheet.writebackRowDescription, styleSheet.writebackRowHint]}>
						{hint}
					</FieldDescription>
				) : null}
			</>
		);
	};

	return (
		<div className={css(styleSheet.settingBlock)}>
			<div className={css(styleSheet.settingHeader)}>
				<h6 className={css(styleSheet.settingTitle)}>Writebacks</h6>
				<div className={css(styleSheet.settingSubtitle)}>
					Levitate activity → the code it&apos;s logged under in {displayName}
				</div>
			</div>
			<div className={css(styleSheet.settingBlockContent)}>
				{rowTypes.length ? (
					rowTypes.map(writeBackType => {
						const toggleId = `writeback-${writeBackType}`;
						const isEnabled = enabledTypes.includes(writeBackType);
						const label = WRITE_BACK_TYPE_LABELS[writeBackType] ?? writeBackType;

						return (
							<div key={writeBackType} className={css(styleSheet.writebackRow)}>
								<div className={css(styleSheet.writebackRowActivity)}>
									<Toggle
										id={toggleId}
										isOn={isEnabled}
										disabled={!isEditing}
										onToggleCheckChanged={checked => setEnabled(writeBackType, checked)}
									/>
									<Label
										htmlFor={toggleId}
										styles={[
											styleSheet.writebackRowLabel,
											isEditing && styleSheet.writebackRowLabelEditable,
											!isEnabled && styleSheet.writebackRowLabelOff,
										]}
									>
										{label}
									</Label>
								</div>
								<div className={css(styleSheet.writebackRowCode)}>{renderCode(writeBackType)}</div>
							</div>
						);
					})
				) : (
					<div className={css(styleSheet.writebackRowMuted)}>No write-back types are available yet.</div>
				)}
			</div>
		</div>
	);
};
